import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, ArrowRight, Lightning, Star } from "@phosphor-icons/react";
import { TOOLS, CATEGORIES, getTool } from "@/data/tools";
import { useFavorites } from "@/hooks/useFavorites";
import FavoriteButton from "@/components/FavoriteButton";

const ToolCard = ({ tool, index }) => {
  const Icon = tool.icon;
  return (
    <Link
      to={`/tool/${tool.slug}`}
      data-testid={`tool-card-${tool.slug}`}
      style={{ animationDelay: `${index * 40}ms` }}
      className="fade-up group relative flex flex-col border-b border-r border-border/70 bg-card p-6 transition-transform duration-150 hover:z-10 hover:-translate-y-[3px] hover:border-primary hover:brutalist-shadow"
    >
      <div className="flex items-start justify-between">
        <span className="grid h-11 w-11 place-items-center border border-border text-primary transition-colors duration-150 group-hover:border-primary group-hover:bg-primary group-hover:text-primary-foreground">
          <Icon size={22} weight="duotone" />
        </span>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] tracking-tight text-muted-foreground">
            {tool.code}
          </span>
          <FavoriteButton slug={tool.slug} className="h-7 w-7" size={14} />
        </div>
      </div>
      <h3 className="mt-5 font-display text-lg font-bold tracking-tight">
        {tool.name}
      </h3>
      <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
        {tool.short}
      </p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.12em] text-primary opacity-0 transition-opacity duration-150 group-hover:opacity-100">
        Open tool <ArrowRight size={14} weight="bold" />
      </span>
    </Link>
  );
};

export default function Home() {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");
  const { favorites } = useFavorites();

  const favTools = useMemo(
    () => favorites.map((s) => getTool(s)).filter(Boolean),
    [favorites]
  );

  const filtered = useMemo(() => {
    return TOOLS.filter((t) => {
      const matchCat = cat === "all" || t.category === cat;
      const q = query.trim().toLowerCase();
      const matchQ =
        !q ||
        t.name.toLowerCase().includes(q) ||
        t.short.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [query, cat]);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/70">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07] bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')",
            maskImage: "linear-gradient(to bottom, black, transparent)",
            WebkitMaskImage: "linear-gradient(to bottom, black, transparent)",
          }}
        />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-32">
          <div className="inline-flex items-center gap-2 border border-border bg-card px-3 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
            <Lightning size={14} weight="fill" className="text-accent" />
            {TOOLS.length} free tools · no sign-up
          </div>
          <h1 className="mt-6 max-w-3xl font-display text-5xl font-extrabold leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
            The precision workbench for the{" "}
            <span className="text-primary">modern web.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            SEO generators, text utilities and everyday web helpers — fast,
            private and built to run instantly in your browser.
          </p>

          <div className="mt-10 flex max-w-xl items-center border border-border bg-card focus-within:ring-2 focus-within:ring-primary">
            <span className="grid h-14 w-14 place-items-center text-muted-foreground">
              <MagnifyingGlass size={20} weight="bold" />
            </span>
            <input
              data-testid="search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tools — try 'meta', 'password', 'qr'..."
              className="h-14 w-full bg-transparent pr-5 text-base outline-none placeholder:text-muted-foreground"
            />
          </div>
        </div>
      </section>

      {/* Favorites bar */}
      {favTools.length > 0 && (
        <section
          data-testid="favorites-bar"
          className="border-b border-border/70 bg-card"
        >
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-6">
            <div className="flex items-center gap-2">
              <Star size={16} weight="fill" className="text-accent" />
              <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
                Your favorites · {favTools.length}
              </h2>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {favTools.map((t) => {
                const FIcon = t.icon;
                return (
                  <div
                    key={t.slug}
                    className="group flex items-center border border-border bg-background transition-colors hover:border-primary"
                  >
                    <Link
                      to={`/tool/${t.slug}`}
                      data-testid={`favorite-chip-${t.slug}`}
                      className="flex items-center gap-2.5 py-2.5 pl-3.5 pr-3 text-sm font-semibold tracking-tight"
                    >
                      <FIcon size={16} weight="duotone" className="text-primary" />
                      {t.name}
                    </Link>
                    <FavoriteButton slug={t.slug} className="h-9 w-9 border-0 border-l border-border" size={14} />
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Category filter */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-10">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`filter-${c.id}`}
              onClick={() => setCat(c.id)}
              className={`border px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] transition-all duration-150 active:scale-[0.97] ${
                cat === c.id
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card text-muted-foreground hover:border-primary hover:text-primary"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* Tools grid */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-6 pt-8">
        {filtered.length === 0 ? (
          <div
            data-testid="no-results"
            className="border border-dashed border-border py-24 text-center"
          >
            <p className="font-display text-xl font-bold">No tools found</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Try a different keyword or category.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 border-l border-t border-border/70 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((tool, i) => (
              <ToolCard key={tool.slug} tool={tool} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
